<template>
  <div class="user-info relative">
    <button
      ref="referenceElement"
      type="button"
      :aria-label="t('common.side-nav.account-access')"
      :aria-controls="popoverId"
      aria-haspopup="dialog"
      :aria-expanded="isPopoverOpen"
      class="user-info-trigger flex w-full items-center gap-1 rounded-sm border border-app-secondary bg-app-secondary-light px-1 py-2 text-left"
      @click="togglePopover"
    >
      <div
        class="flex h-[42px] w-[42px] shrink-0 items-center justify-center overflow-hidden rounded-sm border border-app-grey-light bg-white text-black"
      >
        <img
          v-if="getLogoUrl(props.companyLogoUrl)"
          :src="getLogoUrl(props.companyLogoUrl)"
          :alt="`Logo ${props.companyLabel ?? ''}`"
          loading="lazy"
          class="max-h-8 max-w-8 object-contain"
        />

        <span v-else v-no-capture class="typo-subtitle2 flex h-full w-full items-center justify-center">
          {{ props.companyInitials ?? deriveInitials(props.companyLabel ?? '') }}
        </span>
      </div>

      <div class="flex min-w-0 flex-1 flex-col">
        <p class="typo-text3 truncate tracking-[-0.02em] text-black">
          {{ t('common.side-nav.account-access') }}
        </p>
        <p v-no-capture class="user-info-subtitle typo-subtitle1 line-clamp-2 tracking-[-0.02em] text-black">
          {{ props.companyLabel ?? '' }}
        </p>
      </div>

      <Icon :icon="IconName.Parameter" class="h-5 w-5 shrink-0 text-black" aria-hidden="true" />
    </button>

    <Popover
      v-bind="{ ariaLabel: t('common.side-nav.account-access') }"
      :id="popoverId"
      :is-shown="isPopoverOpen"
      :popover-element-ref="setPopoverElement"
      :style="popoverStyle"
    >
      <div v-no-capture class="flex min-w-0 flex-col gap-0.5 overflow-hidden">
        <p class="user-info-fullname typo-subtitle1 truncate tracking-[-0.02em] text-black">
          {{ props.fullName }}
        </p>
        <p class="typo-text3 truncate tracking-[-0.02em] text-grey">
          {{ props.email }}
        </p>
      </div>

      <div class="h-px w-full shrink-0 bg-app-grey-light" aria-hidden="true" />

      <template v-if="props.selectOptions.length > 0">
        <p class="typo-subtitle1 w-full tracking-[-0.02em] text-black">
          {{ props.selectLabel ?? t('common.side-nav.select-establishment') }}
        </p>

        <div
          v-no-capture
          v-capture-input-name="props.selectTrackingName ?? 'select-client'"
          class="user-info-select flex max-h-60 w-full flex-col gap-1 overflow-y-auto"
          role="listbox"
          :aria-label="props.selectLabel ?? t('common.side-nav.select-establishment')"
        >
          <button
            v-for="option in props.selectOptions"
            :key="option.value"
            type="button"
            role="option"
            class="flex w-full items-center gap-2 overflow-hidden rounded-sm p-2 text-left hover:bg-app-secondary-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-grey"
            :class="option.value === props.selectValue ? 'bg-app-secondary-light' : 'bg-white'"
            :aria-selected="option.value === props.selectValue"
            @click="onSelectUpdate(option.value)"
          >
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-[6px] border border-app-grey-light bg-white text-black"
            >
              <img
                v-if="getLogoUrl(option.logoUrl)"
                :src="getLogoUrl(option.logoUrl)"
                :alt="`Logo ${option.label}`"
                loading="lazy"
                class="max-h-6 max-w-6 object-contain"
              />
              <span v-else class="typo-subtitle2">{{ deriveInitials(option.label) }}</span>
            </span>

            <span
              class="min-w-0 flex-1 tracking-[-0.02em] text-black"
              :class="option.value === props.selectValue ? 'typo-subtitle1' : 'typo-text2'"
            >
              {{ option.label }}
            </span>

            <Icon
              v-if="option.value === props.selectValue"
              :icon="IconName.Checkmark"
              class="h-5 w-5 shrink-0 text-black"
              aria-hidden="true"
            />
          </button>
        </div>

        <div class="h-px w-full shrink-0 bg-app-grey-light" aria-hidden="true" />
      </template>

      <slot name="actions" />

      <LineButton
        v-capture-button-name="'logout'"
        :content="props.logoutLabel ?? t('common.nav.logout')"
        :variant="LineButtonTypes.Secondary"
        :color="LineButtonColor.Error"
        :right-icon="{ icon: IconName.Leave }"
        @click="onLogoutClick"
      />
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { Icon, LineButton, LineButtonColor, LineButtonTypes } from '@kiplin/web-common/design-system';
import { IconName } from '@kiplin/web-common/icon';
import { useId } from 'vue';
import { useI18n } from 'vue-i18n';
import Popover from '@design-system/common/application/component/Popover/Popover.vue';
import { usePopover } from '@design-system/common/application/composable/use-popover.composable';
import type { UserInfoProps } from '@design-system/side-nav/application/component/UserInfo/UserInfo.types';

const props = defineProps<UserInfoProps>();

const emit = defineEmits<{ 'update:selectValue': [value: string]; logout: [] }>();

const { t } = useI18n();
const popoverId = `user-info-popover-${useId()}`;
const { closePopover, isPopoverOpen, popoverStyle, referenceElement, setPopoverElement, togglePopover } = usePopover({
  getGapElement: (reference) => reference.closest('nav'),
  placement: 'right',
});

function onSelectUpdate(value: string): void {
  emit('update:selectValue', value);
  closePopover();
}

function onLogoutClick(): void {
  closePopover();
  emit('logout');
}

function getLogoUrl(url?: string | null): string {
  return url?.trim() ?? '';
}

function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}
</script>
